import React, { useRef, useState } from "react";
import classNames from "classnames";
import { Entity } from "@common/types/entity";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { type SelectNativeInvoiceCustomer } from "./copyNativeInvoicePartyDetails";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { FieldRowMenu } from "./FieldRowMenu";
import { PanelSection } from "./PanelSection";
import { INVOICE_SECTION_ANCHORS } from "./invoiceSectionAnchors";
import { LineItemsSection } from "./LineItemsSection";
import { DetailsFromFields } from "./DetailsFromFields";
import { DetailsToFields } from "./DetailsToFields";
import { CustomizeSection } from "./CustomizeSection";
import { DesignSection } from "./DesignSection";
import {
    EmailDetailsFields,
    SendEmailRecipientFields,
} from "./EmailSectionFields";
import {
    InvoiceFieldSettingsContent,
    type InvoiceFieldSettingsOpenSection,
} from "./InvoiceFieldSettingsPanel";
import type { InvoiceFieldSettingsDraft } from "./invoiceFieldSettings";
import { NativeInvoiceTermsField } from "./NativeInvoiceTermsField";
import { NativeInvoiceCollectPaymentField } from "./NativeInvoiceCollectPaymentField";
import { TemplateNamePanel } from "./TemplateNamePanel";
import {
    TemplateSelectField,
    TemplateSelectProps,
} from "./TemplateSelectField";
import {
    getNativeInvoiceDueDateError,
    withDueDateOverride,
    withInvoiceDateOverride,
} from "./nativeInvoiceFormHelpers";
import { NativeInvoiceLineRow } from "./nativeInvoiceFormTypes";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import styles from "./SidePanel.module.scss";

type AccordionSection =
    | "details"
    | "items"
    | "additionalDetails"
    | "design"
    | "sendEmail";

const DEFAULT_OPEN_SECTIONS: AccordionSection[] = ["details"];

interface Props {
    subtotal: number;
    total: number;
    entities: Entity[];
    selectedEntity?: Entity;
    invoiceId?: string;
    onSelectEntity: (entity: Entity | undefined) => void;
    onSelectCustomer: SelectNativeInvoiceCustomer;
    onChangeLine: (
        lineId: string,
        patch: Partial<NativeInvoiceLineRow>,
    ) => void;
    onAddLine: () => void;
    onRemoveLine: (lineId: string) => void;
    // The From entity can't be changed once an invoice exists (it drives
    // accounting/JE posting and branding), or when catalog items on the
    // invoice are scoped to the selected entity.
    isFromEntityLocked?: boolean;
    fromEntityLockedTooltip?: string;
    hideInvoiceNumber?: boolean;
    // Invoice date / due date are recomputed (from today's date + the
    // template's Terms) every time the template is applied to a new invoice,
    // so any value saved on the template itself is discarded server-side
    // (see NativeInvoiceDocumentCopyInternalService#persistPrototypeContent).
    // Editing them here would silently do nothing, so the fields are hidden
    // entirely in template mode — only Terms drives the due date.
    isTemplateMode?: boolean;
    fieldSettings?: InvoiceFieldSettingsDraft;
    onFieldSettingsChange?: (settings: InvoiceFieldSettingsDraft) => void;
    templateName?: string;
    templateNameError?: string;
    onTemplateNameChange?: (name: string) => void;
    templateNameInputRef?: React.Ref<HTMLInputElement>;
    // Fields & visibility swap-in view (invoice flow). Its open state lives in
    // the workspace so the command bar can switch to Save/Cancel while open;
    // those buttons are the only way out of the view.
    isFieldSettingsViewOpen?: boolean;
    onOpenFieldSettings?: (section: InvoiceFieldSettingsOpenSection) => void;
    fieldSettingsOpenSection?: InvoiceFieldSettingsOpenSection;
    // Template picker group above From; omitted in template mode (a template
    // can't be built from another template).
    templateSelect?: TemplateSelectProps;
}

export const DetailsPanel: React.FC<Props> = ({
    subtotal,
    total,
    entities,
    selectedEntity,
    invoiceId,
    onSelectEntity,
    onSelectCustomer,
    onChangeLine,
    onAddLine,
    onRemoveLine,
    isFromEntityLocked,
    fromEntityLockedTooltip,
    hideInvoiceNumber,
    isTemplateMode,
    fieldSettings,
    onFieldSettingsChange,
    templateName,
    templateNameError,
    onTemplateNameChange,
    templateNameInputRef,
    isFieldSettingsViewOpen,
    onOpenFieldSettings,
    fieldSettingsOpenSection = "from",
    templateSelect,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();

    // Exclusive accordion over the panel sections: opening one closes the one
    // currently open. Details starts open, everything else collapsed. Held
    // here so the open section survives the Settings view swap.
    const [openSections, setOpenSections] = useState<AccordionSection[]>(
        DEFAULT_OPEN_SECTIONS,
    );

    const dueDateError = isTemplateMode
        ? undefined
        : getNativeInvoiceDueDateError(state);

    // Settings never opens with the drawer. Latch once it does so the return
    // trip also fades, without competing with the drawer's first-open zoom.
    const hasViewSwappedRef = useRef(false);
    if (isFieldSettingsViewOpen) {
        hasViewSwappedRef.current = true;
    }
    const panelContentClassName = classNames(
        styles.panelContent,
        hasViewSwappedRef.current && styles.viewSwapFade,
    );

    if (isFieldSettingsViewOpen && fieldSettings && onFieldSettingsChange) {
        return (
            <div key="fieldSettings" className={panelContentClassName}>
                <PanelSection>
                    <div className={styles.unframedSection}>
                        <div className={styles.cardTitle}>Settings</div>
                        <InvoiceFieldSettingsContent
                            draft={fieldSettings}
                            onChange={onFieldSettingsChange}
                            initialOpenSection={fieldSettingsOpenSection}
                            entityId={selectedEntity?.id}
                        />
                    </div>
                </PanelSection>
            </div>
        );
    }

    return (
        <AccordionRoot<AccordionSection>
            key="form"
            value={openSections}
            onValueChange={setOpenSections}
            className={panelContentClassName}
        >
            {onTemplateNameChange && templateName !== undefined && (
                <PanelSection hasCard>
                    <div className={styles.unframedSection}>
                        <TemplateNamePanel
                            name={templateName}
                            error={templateNameError}
                            onNameChange={onTemplateNameChange}
                            inputRef={templateNameInputRef}
                        />
                    </div>
                </PanelSection>
            )}
            {templateSelect && (
                <PanelSection hasCard>
                    <TemplateSelectField {...templateSelect} />
                </PanelSection>
            )}

            <PanelSection
                label="Details"
                value="details"
                headerActions={
                    onOpenFieldSettings && (
                        <FieldRowMenu
                            onOpenSettings={() => onOpenFieldSettings("from")}
                        />
                    )
                }
            >
                <div className={styles.unframedSection}>
                    <div
                        id={INVOICE_SECTION_ANCHORS.fromTo}
                        className={styles.fromToColumns}
                    >
                        <div className={styles.fromToColumn}>
                            <DetailsFromFields
                                entities={entities}
                                selectedEntity={selectedEntity}
                                onSelectEntity={onSelectEntity}
                                isFromEntityLocked={isFromEntityLocked}
                                fromEntityLockedTooltip={
                                    fromEntityLockedTooltip
                                }
                            />
                        </div>
                        <div className={styles.fromToColumn}>
                            <DetailsToFields
                                onSelectCustomer={onSelectCustomer}
                            />
                        </div>
                        <div
                            id={INVOICE_SECTION_ANCHORS.dates}
                            className={styles.fromToColumn}
                        >
                            <div className={styles.groupSubtitleRow}>
                                <div className={styles.groupSubtitleSecondary}>
                                    Payment
                                </div>
                            </div>
                            <div className={styles.fieldGrid}>
                                {/* Wrapper keeps the terms field's hidden
                                    modal from occupying a second grid cell. */}
                                <div>
                                    <NativeInvoiceTermsField />
                                </div>
                                <NativeInvoiceCollectPaymentField
                                    selectedEntity={selectedEntity}
                                    invoiceId={invoiceId}
                                />
                            </div>
                            {!isTemplateMode && (
                                <div className={styles.fieldGrid}>
                                    <Field
                                        label="Invoice date"
                                        control="button"
                                    >
                                        <Field.Control
                                            render={(controlProps) => (
                                                <DatePicker
                                                    mode="day"
                                                    variant="panel-input"
                                                    className={
                                                        styles.datePicker
                                                    }
                                                    clearable={false}
                                                    value={
                                                        state.invoiceDate ||
                                                        undefined
                                                    }
                                                    onValueChange={(value) =>
                                                        setState((prev) =>
                                                            withInvoiceDateOverride(
                                                                prev,
                                                                value ?? "",
                                                            ),
                                                        )
                                                    }
                                                    placeholder="Select"
                                                    buttonProps={{
                                                        variant: "primary",
                                                        size: "xl",
                                                    }}
                                                    fieldControlProps={pickFieldControlAriaProps(
                                                        controlProps,
                                                    )}
                                                />
                                            )}
                                        />
                                    </Field>
                                    <Field
                                        label="Due date"
                                        control="button"
                                        invalid={dueDateError !== undefined}
                                    >
                                        <Field.Control
                                            render={(controlProps) => (
                                                <DatePicker
                                                    mode="day"
                                                    variant="panel-input"
                                                    className={
                                                        styles.datePicker
                                                    }
                                                    clearable
                                                    minDate={
                                                        state.invoiceDate ||
                                                        undefined
                                                    }
                                                    value={
                                                        state.dueDate ||
                                                        undefined
                                                    }
                                                    onValueChange={(value) =>
                                                        setState((prev) =>
                                                            withDueDateOverride(
                                                                prev,
                                                                value ?? "",
                                                            ),
                                                        )
                                                    }
                                                    placeholder="Select"
                                                    buttonProps={{
                                                        variant: "primary",
                                                        size: "xl",
                                                    }}
                                                    fieldControlProps={pickFieldControlAriaProps(
                                                        controlProps,
                                                    )}
                                                />
                                            )}
                                        />
                                    </Field>
                                </div>
                            )}
                            {(!hideInvoiceNumber || !isTemplateMode) && (
                                <div className={styles.fieldGrid}>
                                    {!hideInvoiceNumber && (
                                        <Field label="Invoice number">
                                            <Input
                                                size="xl"
                                                maxLength={255}
                                                value={state.invoiceNumber}
                                                onChange={(event) =>
                                                    setState((prev) => ({
                                                        ...prev,
                                                        invoiceNumber:
                                                            event.target.value,
                                                    }))
                                                }
                                            />
                                        </Field>
                                    )}
                                    {!isTemplateMode && (
                                        <Field label="PO number">
                                            <Input
                                                size="xl"
                                                maxLength={255}
                                                value={state.poNumber}
                                                onChange={(event) =>
                                                    setState((prev) => ({
                                                        ...prev,
                                                        poNumber:
                                                            event.target.value,
                                                    }))
                                                }
                                            />
                                        </Field>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </PanelSection>

            <PanelSection
                label="Items"
                id={INVOICE_SECTION_ANCHORS.items}
                value="items"
                hasFlushBody
                headerActions={
                    onOpenFieldSettings && (
                        <FieldRowMenu
                            onOpenSettings={() => onOpenFieldSettings("items")}
                        />
                    )
                }
            >
                <LineItemsSection
                    lines={state.lines}
                    entityId={selectedEntity?.id}
                    subtotal={subtotal}
                    total={total}
                    onChangeLine={onChangeLine}
                    onAddLine={onAddLine}
                    onRemoveLine={onRemoveLine}
                />
            </PanelSection>

            <PanelSection
                label="Notes"
                id={INVOICE_SECTION_ANCHORS.additionalDetails}
                value="additionalDetails"
                headerActions={
                    onOpenFieldSettings && (
                        <FieldRowMenu
                            onOpenSettings={() => onOpenFieldSettings("notes")}
                        />
                    )
                }
            >
                <CustomizeSection />
            </PanelSection>

            <PanelSection
                label="Design"
                id={INVOICE_SECTION_ANCHORS.design}
                value="design"
                headerActions={
                    onOpenFieldSettings && (
                        <FieldRowMenu
                            onOpenSettings={() => onOpenFieldSettings("header")}
                        />
                    )
                }
            >
                <DesignSection selectedEntity={selectedEntity} />
            </PanelSection>

            {isTemplateMode && (
                <PanelSection label="Email" value="sendEmail">
                    <div className={styles.unframedSection}>
                        <SendEmailRecipientFields />
                        <EmailDetailsFields isTemplateMode />
                    </div>
                </PanelSection>
            )}
        </AccordionRoot>
    );
};
